import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useRef } from "react";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { loadSidechain } from "./loadSidechain";
import { Notification } from "common/components/Notification";
import { Bugsnag } from "common/lib/bugsnag";

// Importing @nprapps/sidechain above creates a custom element, which requires
// us to define a type for it so typescript doesn't complain.
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "side-chain": {
        src: string;
        ref?: React.RefObject<HTMLElement | null>;
      };
    }
  }
}
export interface IframeProps {
  src: string;
  height?: number;
  title?: string;
}
export const Iframe = t0 => {
  "use memo";

  const $ = _c(18);
  const {
    src,
    height,
    title
  } = t0;
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const sidechainRef = useRef(null);
  const t = useTranslations("blocks.iframes");
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      let cancelled = false;
      loadSidechain().then(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      }).catch(error => {
        Bugsnag.notify(error as Error, _temp);
        if (!cancelled) {
          setLoadFailed(true);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== loaded || $[3] !== t || $[4] !== title) {
    t3 = () => {
      if (!loaded || !sidechainRef.current) {
        return;
      }
      const sidechainEl = sidechainRef.current;
      const updateIframeTitle = () => {
        const iframe = sidechainEl.shadowRoot?.querySelector("iframe");
        if (iframe && iframe.getAttribute("title") !== title) {
          iframe.setAttribute("title", title || t("default_accessible_title"));
        }
      };
      updateIframeTitle();
      const targetNode = sidechainEl.shadowRoot || sidechainEl;
      const observer = new MutationObserver(() => {
        updateIframeTitle();
      });
      observer.observe(targetNode, {
        childList: true,
        subtree: true
      });
      return () => {
        observer.disconnect();
      };
    };
    t4 = [loaded, title, t];
    $[2] = loaded;
    $[3] = t;
    $[4] = title;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (loaded) {
    let t5;
    if ($[7] !== src) {
      t5 = <side-chain ref={sidechainRef} src={src} />;
      $[7] = src;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  } else {
    if (loadFailed) {
      let t5;
      if ($[9] !== t) {
        t5 = t("load_failed_title");
        $[9] = t;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      let t6;
      if ($[11] !== t) {
        t6 = t("load_failed_message");
        $[11] = t;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      let t7;
      if ($[13] !== t5 || $[14] !== t6) {
        t7 = <div className="mb-4"><Notification type="info" title={t5} message={t6} closable={false} /></div>;
        $[13] = t5;
        $[14] = t6;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      return t7;
    } else {
      const t5 = height || 0;
      let t6;
      if ($[16] !== t5) {
        t6 = <div aria-busy="true" style={{
          minHeight: t5
        }} />;
        $[16] = t5;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      return t6;
    }
  }
};
function _temp(event) {
  event.severity = "info";
}